import { apiClient, apiWrapper } from './api';
import { ExtractionProfile, Tenant, TenantSettings } from '../types';

export interface TenantSettingsUpdateRequest {
  sendgrid_api_key?: string;
  sendgrid_sender?: string;
  sendgrid_sender_name?: string;
  twilio_account_sid?: string;
  twilio_auth_token?: string;
  twilio_number?: string;
  // A05: null = Feld im Formular geleert (Speichern ist dann gesperrt)
  default_cutoff_hours?: number | null;
  default_slot_length_minutes?: number | null;
  default_max_slots_per_day?: number | null;
  default_max_bookings_per_slot?: number | null;
  locale?: string;
  timezone?: string;
  primary_color?: string;
  logo_url?: string;
  // Support contact (public booking page)
  support_email?: string;
  support_phone?: string;
  enable_sms_notifications?: boolean;
  enable_email_notifications?: boolean;
  enable_auto_confirm?: boolean;
  require_phone_verification?: boolean;
  business_hours?: Record<string, any>;
  // PDF/OCR Settings
  azure_form_recognizer_endpoint?: string;
  azure_form_recognizer_key?: string;
  azure_form_recognizer_model_id?: string;
  graph_client_id?: string;
  graph_client_secret?: string;
  graph_tenant_id?: string;
  graph_shared_mailbox?: string;
  graph_inbox_folder?: string;
  // Gemini API Settings
  gemini_api_key?: string;
  use_gemini_extraction?: boolean;
  gemini_model_name?: string;
  extraction_profile?: ExtractionProfile | null;
  // PDF Processing Settings
  enable_pdf_auto_processing?: boolean;
  pdf_processing_interval_minutes?: number;
  pdf_confidence_threshold?: number;
  pdf_filename_filter?: string;
  mail_folder_processed?: string;
  mail_folder_error?: string;
  // Webhook Settings
  webhook_url?: string;
  webhook_secret?: string;
  webhook_enabled?: boolean;
  default_language?: string;
  // Invite Reminder Settings
  enable_invite_reminder?: boolean;
  invite_reminder_hours?: number | null;
  // Calendar integration settings
  calendar_mailbox?: string;
  enable_calendar_sync?: boolean;
  send_confirm_email_with_calendar?: boolean;
  send_cancel_email_with_calendar?: boolean;
  // FEAT-01: Cancel notification toggle
  enable_cancel_notification?: boolean;
  // Subcontractor & Delivery Note settings
  enable_subcontractors?: boolean;
  subcontractor_auto_assign?: boolean;
  enable_delivery_notes?: boolean;
  delivery_note_footer?: string;
  depot_postal_code?: string;
  /** P-19: Positionen-Erfassung im Auftrag (UI-Opt-in) */
  enable_order_items?: boolean;
  /** P-20: Adress-Autocomplete via geo.admin.ch */
  enable_address_autocomplete?: boolean;
  // Read-only: encryption health (populated from API response)
  decryption_warnings?: string[];
  // Read-only: server-side "secret is set" flags (never sent back)
  sendgrid_api_key_configured?: boolean;
  twilio_account_sid_configured?: boolean;
  twilio_auth_token_configured?: boolean;
  azure_form_recognizer_key_configured?: boolean;
  graph_client_id_configured?: boolean;
  graph_client_secret_configured?: boolean;
  gemini_api_key_configured?: boolean;
  webhook_secret_configured?: boolean;
}

export const tenantService = {
  // Get all tenants (global admin only, or current tenant for regular users)
  async getTenants(): Promise<{ tenants: Tenant[] }> {
    return apiWrapper(() => apiClient.get<{ tenants: Tenant[] }>('/tenants'));
  },

  // Get single tenant
  async getTenant(id: string): Promise<{ tenant: Tenant }> {
    return apiWrapper(() => apiClient.get<{ tenant: Tenant }>(`/tenants/${id}`));
  },

  // Create tenant (global admin only)
  async createTenant(data: {
    key: string;
    name: string;
    subdomain: string;
    active?: boolean;
  }): Promise<{ tenant: Tenant }> {
    return apiWrapper(() => apiClient.post<{ tenant: Tenant }>('/tenants', data));
  },

  // Update tenant (global admin only)
  async updateTenant(
    id: string,
    data: Partial<{
      key: string;
      name: string;
      subdomain: string;
      active: boolean;
    }>,
  ): Promise<{ tenant: Tenant }> {
    return apiWrapper(() => apiClient.patch<{ tenant: Tenant }>(`/tenants/${id}`, data));
  },

  // Get tenant settings
  async getTenantSettings(tenantId: string): Promise<{ settings: TenantSettings }> {
    return apiWrapper(() => apiClient.get<{ settings: TenantSettings }>(`/tenants/${tenantId}/settings`));
  },

  // Update tenant settings
  async updateTenantSettings(
    tenantId: string,
    data: TenantSettingsUpdateRequest,
  ): Promise<{ settings: TenantSettings }> {
    return apiWrapper(() => apiClient.put<{ settings: TenantSettings }>(`/tenants/${tenantId}/settings`, data));
  },

  // Test webhook endpoint
  async testWebhook(tenantId: string): Promise<{ message: string }> {
    return apiWrapper(() => apiClient.post<{ message: string }>(`/tenants/${tenantId}/settings/webhook/test`));
  },

  // P-16: Default extraction profile (== behaviour when extraction_profile is null)
  async getDefaultExtractionProfile(
    tenantId: string,
  ): Promise<{ profile: ExtractionProfile; prompt_template_version: string }> {
    return apiWrapper(() =>
      apiClient.get<{ profile: ExtractionProfile; prompt_template_version: string }>(
        `/tenants/${tenantId}/settings/extraction-profile/default`,
      ),
    );
  },

  // P-16: Render the Gemini prompt for a (possibly unsaved) profile
  async previewExtractionPrompt(
    tenantId: string,
    profile: ExtractionProfile | null,
  ): Promise<{ prompt: string; document_types: string[]; prompt_template_version: string }> {
    return apiWrapper(() =>
      apiClient.post<{ prompt: string; document_types: string[]; prompt_template_version: string }>(
        `/tenants/${tenantId}/settings/extraction-profile/preview`,
        { profile },
      ),
    );
  },

  // Upload tenant logo (multipart/form-data)
  async uploadLogo(file: File): Promise<{ logo_url: string }> {
    const formData = new FormData();
    formData.append('logo', file);
    return apiWrapper(() =>
      apiClient.post<{ logo_url: string }>('/tenants/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  // Delete tenant logo
  async deleteLogo(): Promise<void> {
    return apiWrapper(() => apiClient.delete('/tenants/settings/logo'));
  },

  // Get logo URL for use as img src (relative path)
  getLogoUrl(tenantId: string): string {
    return `/api/tenants/${tenantId}/logo`;
  },
};
